import type { Invoice } from "@medplum/fhirtypes";

import { SERVICE_PAYER_CODE } from "@/billing/constants/service-payer-code";
import { getInvoiceServicePayerCode } from "@/billing/utils/service-payer-code";

import { STATEMENT_SCOPE } from "../../../constants/statements";
import { StatementScope } from "../../../types/statements";
import {
  STATEMENT_ACCEPTED_SUB_STATUS_OPTIONS,
  STATEMENT_STATUS,
  StatementStatus,
} from "../constants/statements";
import { ARCHIVE_STATUS, DELETED_VIEW_VALUE } from "../constants/statements-filter";
import { StatementsFilters } from "../types/statements-filter";

import { getStatementStatus } from "./statements";

const STATEMENT_STATUS_VALUES = new Set<string>(Object.values(STATEMENT_STATUS));

function isStatementStatus(value: string): value is StatementStatus {
  return STATEMENT_STATUS_VALUES.has(value);
}

/**
 * Splits the flat checkbox-group selection into the status list and the two scope flags.
 *
 * The deleted checkbox carries the `DeletedOnly` scope token rather than the literal word, so it
 * lands on the `deleted` flag instead of being picked up as the Cancelled badge status, which reads
 * as "Deleted" too.
 */
export function parseFilterValues(values: string[]): StatementsFilters {
  return {
    statuses: values.filter(isStatementStatus),
    archived: values.includes(ARCHIVE_STATUS.Archived),
    deleted: values.includes(DELETED_VIEW_VALUE),
  };
}

/** The inverse of `parseFilterValues`, for seeding the drawer's draft selection. */
export function toFilterValues(filters: StatementsFilters): string[] {
  return [
    ...filters.statuses,
    ...(filters.archived ? [ARCHIVE_STATUS.Archived] : []),
    ...(filters.deleted ? [DELETED_VIEW_VALUE] : []),
  ];
}

/**
 * The scope the filter selection resolves to. Deleted takes precedence over archived: the deleted
 * view spans archived and unarchived statements alike, so selecting both reads as the wider view
 * rather than an empty intersection.
 */
export function getStatementScope(filters: StatementsFilters): StatementScope {
  if (filters.deleted) return STATEMENT_SCOPE.DeletedOnly;
  if (filters.archived) return STATEMENT_SCOPE.ArchivedOnly;
  return STATEMENT_SCOPE.Active;
}

const ACCEPTED_BRANCH_STATUSES = new Set<StatementStatus>(STATEMENT_ACCEPTED_SUB_STATUS_OPTIONS);

/**
 * Whether the statement is on the accepted branch that the `Accepted` filter rolls up.
 *
 * `Accepted` is a rollup rather than a badge of its own: `getStatementStatus` never returns it,
 * because an accepted statement immediately reads as `In Processing` and moves on to `Paid` or
 * `Denied` from there. Matching it literally would always return nothing.
 *
 * The branch is insurance-only — the `accepted` tag is derived from a ClaimResponse and is never
 * set on a self-pay statement, which reaches `Paid` or `Denied` without ever being accepted. So the
 * rollup also checks the payer; without that, a self-pay `Paid` statement that was never accepted
 * would answer to the `Accepted` filter.
 */
function matchesAcceptedRollup(invoice: Invoice, status: StatementStatus): boolean {
  return (
    ACCEPTED_BRANCH_STATUSES.has(status) &&
    getInvoiceServicePayerCode(invoice) === SERVICE_PAYER_CODE.Insurance
  );
}

/**
 * Narrows the fetched statements by badge status.
 *
 * Status matching happens here rather than in the search query because the badge status is derived
 * from `Invoice.status` plus a meta.tag: `Sent` means "issued with no status tag", which no single
 * positive search param expresses, and an `accepted` tag renders as `In Processing`. Filtering
 * server-side would return rows whose badges disagree with the selected filter.
 *
 * The archive and delete filters are applied by the query instead: each scope returns one slice of
 * statements, so the excluded ones never reach this function to be narrowed.
 *
 * Status narrowing is skipped entirely under the deleted view: every deleted statement reads as
 * Cancelled, so any status pairing would match nothing. The drawer already clears the statuses when
 * the view is turned on; this keeps a filter set built any other way from emptying the list.
 */
export function filterStatements(invoices: Invoice[], filters: StatementsFilters): Invoice[] {
  if (filters.deleted || filters.statuses.length === 0) return invoices;

  const selected = new Set<StatementStatus>(filters.statuses);
  const rollUpAccepted = selected.has(STATEMENT_STATUS.Accepted);

  return invoices.filter((invoice) => {
    const status = getStatementStatus(invoice);

    return selected.has(status) || (rollUpAccepted && matchesAcceptedRollup(invoice, status));
  });
}

/** How many filters are applied, for the count badge on the Filter button. */
export function countActiveFilters(filters: StatementsFilters): number {
  return filters.statuses.length + (filters.archived ? 1 : 0) + (filters.deleted ? 1 : 0);
}
